import React, { useState } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { ReactComponent as LinkedinIcon } from "../../../assets/svg/linkedInLogoSvg.svg";
import Button from "../common/Button";
import linkedInUtils from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/common/linkedInUtils";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "../common/toaster";

class AddLinkedInAccountPopupState {
  @observable showPopup = false;
  @observable cbk = () => {};
  @observable message = "";

  @action
  setShowPopup = (value = false) => {
    this.showPopup = value;
  };
}

const addLinkedInAccountPopupState = new AddLinkedInAccountPopupState();

const AddLinkedInAccountPopup = observer(() => {
  // "chooseType" -> the entry screen with Connect LinkedIn / Send an invite
  // "invite" -> enter an email to send the connect link to someone else
  // "inviteSent" -> confirmation after the invite email goes out
  const [view, setView] = useState("chooseType");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  if (!addLinkedInAccountPopupState?.showPopup) {
    return null;
  }

  const closePopup = () => {
    addLinkedInAccountPopupState.setShowPopup(false);
    setView("chooseType");
    setInviteEmail("");
    setSendingInvite(false);
  };

  const handleContinue = () => {
    // Account type (standard vs. Sales Navigator) is now detected by the
    // backend during connect - no user choice needed here.
    linkedInUtils.handleButtonClick();
    closePopup();
  };

  const handleSendInvite = async () => {
    if (!inviteEmail?.trim() || sendingInvite) {
      return;
    }
    setSendingInvite(true);
    const config = {
      url: URLS.sendLinkedInConnectInvite,
      method: "POST",
      data: { toAddress: inviteEmail.trim() }
    };
    const response = await makeApi(config);
    setSendingInvite(false);
    if (response?.data?.status) {
      setView("inviteSent");
    } else {
      toasterState.setToastMsg(
        "Something went wrong while sending the invite. Please try again.",
        "fail"
      );
    }
  };

  const renderChooseType = () => (
    <>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Connect LinkedIn</div>
          <div className="chooseAccountType">
            Authorize your LinkedIn account to get started
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInAccountPopupFooter">
          <Button
            id="continueLinkedInBtn"
            name="continueLinkedInBtn"
            btnText="Connect LinkedIn"
            className="continueLinkedInBtn"
            type="button"
            click={handleContinue}
          />
        </div>
        <div className="sendInviteDivider">
          <span>or</span>
        </div>
        <div
          className="sendInviteInsteadLink"
          onClick={() => setView("invite")}
        >
          Send an invite instead
        </div>
      </div>
    </>
  );

  const renderInvite = () => (
    <>
      <div
        className="linkedInInviteBackLink"
        onClick={() => setView("chooseType")}
      >
        <i className="material-icons">arrow_back</i>
        Back
      </div>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Send invite link</div>
          <div className="chooseAccountType">
            We'll email a one-time LinkedIn connect link to this address
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInInviteEmailField">
          <label htmlFor="linkedInInviteEmail">Email address</label>
          <input
            id="linkedInInviteEmail"
            type="email"
            placeholder="teammate@company.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
          />
        </div>
        <div className="linkedInAccountPopupFooter">
          <Button
            id="sendLinkedInInviteBtn"
            name="sendLinkedInInviteBtn"
            btnText="Send invite"
            className={`continueLinkedInBtn${
              !inviteEmail?.trim() ? " disabled" : ""
            }${sendingInvite ? " loading" : ""}`}
            type="button"
            loader={sendingInvite}
            click={handleSendInvite}
          />
        </div>
      </div>
    </>
  );

  const renderInviteSent = () => (
    <div className="linkedInInviteSent">
      <i className="material-icons">check_circle</i>
      <div className="headingTitle">Invite sent</div>
      <div className="chooseAccountType">
        We emailed the connect link to {inviteEmail}. It'll show up as
        pending until they finish authorizing.
      </div>
      <div className="linkedInAccountPopupFooter">
        <Button
          id="doneLinkedInInviteBtn"
          name="doneLinkedInInviteBtn"
          btnText="Done"
          className="continueLinkedInBtn"
          type="button"
          click={closePopup}
        />
      </div>
    </div>
  );

  return (
    <div className="commonPopupWrapper linkedInAccountPopupWrapper">
      <div className="common-popup-mask" />
      <div className="commonPopupContainer linkedInAccountPopupContainer animated customzoomIn">
        <div className="closeIconContainer">
          <i
            className="material-icons commonPopupCloseIcon"
            onClick={closePopup}
          >
            close
          </i>
        </div>
        {view === "chooseType" && renderChooseType()}
        {view === "invite" && renderInvite()}
        {view === "inviteSent" && renderInviteSent()}
      </div>
    </div>
  );
});

export { addLinkedInAccountPopupState };
export default AddLinkedInAccountPopup;
